import React from 'react';
import { playStoreUrl } from '../lib/appLinks';

interface AppPageProps {
  onBackToHome: () => void;
  onNavigateToDictionary: () => void;
  onStartAiInterpretation: (symbolName?: string) => void;
}

const preventDefault = (e: React.MouseEvent, handler: () => void) => {
  if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) {
    e.preventDefault();
    handler();
  }
};

const APP_STORE_URL = playStoreUrl('app_page');

const APP_FEATURES: { icon: string; title: string; text: string }[] = [
  {
    icon: '🤖',
    title: 'تفسير الحلم كاملاً بالذكاء الاصطناعي',
    text: 'اكتب أو أملِ حلمك كما هو، فيقوم الذكاء الاصطناعي بربط جميع الرموز والتفاصيل بظروفك الشخصية وتقديم تفسير مركّب متكامل للرؤيا، لا مجرد دلالة رمز مفرد.'
  },
  {
    icon: '🕌',
    title: 'اختيار المفسر والمنهج',
    text: 'اختر المنهج أو المفسر الذي تريد التفسير على أساسه عند توفره (ابن سيرين، النابلسي وغيرهما) ليصبح التفسير مطابقاً لاختيارك.'
  },
  {
    icon: '📖',
    title: 'بحث مكتبة الرموز التراثية',
    text: 'نفس قاموس الرموز الشامل الموجود في الموقع، مبني داخل التطبيق لتصل إلى دلالة أي رمز بنقرة واحدة حتى أثناء التفسير.'
  },
  {
    icon: '🎙️',
    title: 'الإدخال الصوتي',
    text: 'لا ترغب بالكتابة؟ أملِ رؤياك بصوتك ويحوّلها التطبيق إلى نص يحلله الذكاء الاصطناعي فوراً.'
  },
  {
    icon: '🕰️',
    title: 'سجل التفسيرات',
    text: 'كل تفسيرك محفوظة في سجلّك تعود إليها في أي وقت، فتابع رؤاك وتفاصيلها دون أن تفقد شيئاً.'
  },
  {
    icon: '⭐',
    title: 'المفضلة',
    text: 'احفظ التفسيرات والرموز المهمة لك في قائمة مفضلة لترجع إليها بسرعة متى شئت.'
  }
];

const APP_STEPS: { icon: string; title: string; text: string }[] = [
  { icon: '📝', title: 'اكتب أو أملِ حلمك', text: 'صف رؤياك كما رأيتها بكلماتك، أو استخدم الإدخال الصوتي إن كان أسرع لديك.' },
  { icon: '⚙️', title: 'حدّد بياناتك واختر المفسر', text: 'أدخل حالتك وعمرك واختر المنهج أو المفسر المناسب عند توفره.' },
  { icon: '✨', title: 'احصل على التفسير الكامل', text: 'تصلك رؤيا مفسرة كاملة، تُجمع رموزها وترتبط بظروفك وفق المنهج التراثي.' },
  { icon: '🔖', title: 'احفظ وارجع لاحقاً', text: 'يُحفظ تفسيرك في سجلّك تلقائياً وتضيف ما يهمك إلى مفضلتك في أي وقت.' }
];

const APP_FAQ: { q: string; a: string }[] = [
  {
    q: 'ما الفرق بين موقع ابن سيرين وتطبيق الأندرويد؟',
    a: 'الموقع موسوعة مفتوحة تشرح دلالة الرمز الواحد بالتفصيل (مثل الثعبان، الذهب، البحر) للقراءة والبحث. أما التطبيق فيفسّر الحلم كاملاً: تكتب رؤياك بكل رموزها فيجمعها الذكاء الاصطناعي في تفسير متكامل مرتبط بظروفك، مع الإدخال الصوتي والسجل والمفضلة.'
  },
  {
    q: 'هل تحميل التطبيق مجاني؟',
    a: 'نعم، التطبيق متاح للتحميل مجاناً من متجر Google Play ويشمل ميزات التفسير بالذكاء الاصطناعي والبحث في المكتبة التراثية.'
  },
  {
    q: 'من أين أنزّل التطبيق الرسمي؟',
    a: 'رابط التحميل الرسمي الموثق هو عبر متجر Google Play بعنوان "تفسير الأحلام لابن سيرين و AI". ننصح دائماً بالتحميل من المتجر الرسمي للحصول على آخر إصدار.'
  },
  {
    q: 'هل يعتمد التفسير في التطبيق على مصادر تراثية موثقة؟',
    a: 'نعم، يُبنى التفسير على المادة التراثية المنقولة عن أهل التعبير (ابن سيرين والنابلسي وغيرهما مما تتوفر له مادة)، ويُذكر بكل تفسير أنه اجتهاد آلي لا يُنسب مباشرة لأحد المفسرين.'
  }
];

const APP_SCREENSHOTS: { src: string; width: number; height: number; alt: string; caption: string }[] = [
  {
    src: '/Screenshot (1).webp',
    width: 244,
    height: 128,
    alt: 'لقطة شاشة حقيقية من تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد — واجهة تفسير الحلم بالذكاء الاصطناعي',
    caption: 'ابدأ فوراً بتفسير حلمك الكامل بالذكاء الاصطناعي من الشاشة الرئيسية للتطبيق.'
  },
  {
    src: '/Screenshot (2).webp',
    width: 244,
    height: 128,
    alt: 'لقطة شاشة حقيقية من تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد — إدخال نص الحلم وبيانات الرائي والإدخال الصوتي',
    caption: 'اكتب رؤياك كما رأيتها، أو أملِها بصوتك مع بياناتك الجنس والعمر والحالة.'
  },
  {
    src: '/Screenshot (3).webp',
    width: 244,
    height: 128,
    alt: 'لقطة شاشة حقيقية من تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد — جلسة التفسير مع اختيار المفسر والمنهج',
    caption: 'اختر المفسر أو المنهج الذي تطمئن له: ابن سيرين أو النابلسي أو ابن شاهين الظاهري.'
  },
  {
    src: '/Screenshot (4).webp',
    width: 244,
    height: 128,
    alt: 'لقطة شاشة حقيقية من تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد — نتيجة التفسير الكامل للحلم المربوط بظروف الرائي',
    caption: 'استلم تفسيراً مركّباً يجمع رموز رؤياك كلها وفق المنهج التراثي.'
  },
  {
    src: '/Screenshot (5).webp',
    width: 244,
    height: 242,
    alt: 'لقطة شاشة حقيقية من تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد — الإدخال الصوتي وسجل التفسيرات والمفضلة',
    caption: 'احفظ تفسيرك في سجلّك وأضِف ما يهمك إلى المفضلة، مع الإدخال الصوتي للمريح.'
  }
];

const AppScreenshotCard: React.FC<{ shot: (typeof APP_SCREENSHOTS)[number] }> = ({ shot }) => (
  <figure className="group overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-800/50 shadow-xl transition-all hover:border-emerald-500/60 hover:shadow-emerald-900/20">
    <div className="overflow-hidden border-b border-slate-700/40 bg-slate-900">
      <img
        src={shot.src}
        alt={shot.alt}
        width={shot.width}
        height={shot.height}
        loading="lazy"
        decoding="async"
        className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-[1.03]"
      />
    </div>
    <figcaption className="px-4 pb-4 pt-3 text-sm text-slate-300 leading-relaxed">
      <span className="text-[11px] text-emerald-400 font-bold block mb-1">📱 لقطة حقيقية من تطبيق الأندرويد</span>
      {shot.caption}
    </figcaption>
  </figure>
);

const AppPage: React.FC<AppPageProps> = ({ onBackToHome, onNavigateToDictionary, onStartAiInterpretation }) => {
  return (
    <div className="min-h-screen w-full bg-[#1E293B] text-slate-100 py-8 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-5xl mx-auto">
        <nav aria-label="مسار التصفح" className="flex items-center gap-2 text-sm text-slate-300 mb-8 pb-4 border-b border-slate-700/60">
          <a href="/" onClick={(e) => preventDefault(e, onBackToHome)} className="hover:text-emerald-400 transition-colors no-underline">الرئيسية</a>
          <span>/</span>
          <span className="text-emerald-400 font-semibold">تحميل التطبيق</span>
        </nav>

        <header className="text-center mb-16">
          <div className="mb-6 inline-flex p-4 bg-emerald-500/10 rounded-3xl border border-emerald-500/20 shadow-2xl">
            <img src="/ibn-sirin.png" alt="تطبيق ابن سيرين لتفسير الأحلام" width="112" height="112" className="w-20 h-20 sm:w-28 sm:h-28 drop-shadow-[0_0_18px_rgba(16,185,129,0.35)]" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            تطبيق تفسير الأحلام لابن سيرين
            <span className="block text-emerald-400 text-xl sm:text-2xl mt-3">والذكاء الاصطناعي</span>
          </h1>
          <p className="text-lg sm:text-xl font-medium mb-3 max-w-2xl mx-auto leading-relaxed">
            الموقع يساعدك على فهم <span className="text-emerald-400 font-bold">الرمز</span>، والتطبيق يساعدك على فهم <span className="text-emerald-400 font-bold">الحلم كاملاً</span>.
          </p>
          <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto mb-8 leading-relaxed">
            اكتب رؤياك بكل رموزها وتفاصيلها ويجمع الذكاء الاصطناعي تفسيرها المركّب وفق المنهج التراثي، مع اختيار المفسر والبحث في المكتبة والإدخال الصوتي والسجل والمفضلة.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-lg mx-auto">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto flex-1 py-4 px-8 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-lg rounded-2xl shadow-xl shadow-emerald-900/30 transition-all active:scale-95 text-center no-underline"
            >
              📱 تحميل التطبيق من Google Play
            </a>
            <a
              href="/?ai=1"
              onClick={(e) => preventDefault(e, () => onStartAiInterpretation())}
              className="w-full sm:w-auto py-4 px-6 bg-slate-800 hover:bg-slate-700 text-emerald-400 font-bold text-base rounded-2xl border border-slate-700 hover:border-emerald-500/50 transition-all text-center no-underline"
            >
              جرّب التفسير الآن على الويب
            </a>
          </div>
        </header>
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">لقطات شاشة حقيقية من التطبيق</h2>
            <p className="text-slate-300 text-base max-w-2xl mx-auto leading-relaxed">
              مشاهد حقيقية من واجهة تطبيق تفسير الأحلام لابن سيرين و AI للأندرويد كما تظهر على متجر Google Play — لقطات فعلية من شاشات التفسير بالذكاء الاصطناعي، دون أي صور مركّبة أو وهمية.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {APP_SCREENSHOTS.map((s) => (
              <AppScreenshotCard key={s.src} shot={s} />
            ))}
          </div>
        </section>

        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">ماذا يفعل تطبيق ابن سيرين؟</h2>
            <p className="text-slate-300 text-base max-w-2xl mx-auto">
              الموقع يشرح دلالة الرمز الواحد، أما التطبيق فمنهج كامل لتفسير الحلم بكل رموزه وتفاصيله.
              ولتصفح قاموس الرموز على الويب: <a href="/dictionary" onClick={(e) => preventDefault(e, onNavigateToDictionary)} className="text-emerald-400 hover:text-emerald-300 font-semibold no-underline">قاموس الأحلام</a>.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {APP_FEATURES.map((f) => (
              <article key={f.title} className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-6 transition-all hover:border-emerald-500/60 hover:shadow-xl">
                <span className="text-3xl block mb-4">{f.icon}</span>
                <h3 className="font-bold text-white text-base mb-2">{f.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{f.text}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="mb-16">
          <div className="bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-900 border border-emerald-500/30 rounded-3xl p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 text-center">كيف تستخدم التطبيق؟</h2>
            <p className="text-slate-300 text-center text-sm sm:text-base mb-10 max-w-xl mx-auto">أربع خطوات بسيطة تفصلك عن تفسير كامل لحلمك.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {APP_STEPS.map((s, i) => (
                <div key={s.title} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-emerald-500/15 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-2xl">{s.icon}</div>
                  <div className="text-[11px] text-emerald-400 font-bold mb-1">الخطوة {i + 1}</div>
                  <h3 className="font-bold text-white text-sm mb-1.5">{s.title}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-6 text-center">أسئلة شائعة عن التطبيق</h2>
          <div className="max-w-2xl mx-auto space-y-4">
            {APP_FAQ.map((f) => (
              <details key={f.q} className="bg-slate-800/50 border border-slate-700/60 rounded-xl p-4 group">
                <summary className="font-bold text-white cursor-pointer hover:text-emerald-400 transition-colors flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <span className="text-emerald-400 text-xs transition-transform group-open:rotate-180">▼</span>
                </summary>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed border-t border-slate-700/50 pt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
        <section className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/30 rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">فسّر حلمك كاملاً — أينما كنت</h2>
          <p className="text-slate-300 text-sm sm:text-base mb-8 max-w-xl mx-auto leading-relaxed">
            حمّل تطبيق تفسير الأحلام لابن سيرين والذكاء الاصطناعي مجاناً على جهاز الأندرويد وابدأ برؤياك الأولى اليوم.
          </p>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold rounded-2xl shadow-xl shadow-emerald-900/30 transition-all active:scale-95 no-underline text-center"
          >
            📱 تحميل التطبيق من Google Play
          </a>
          <div className="mt-6 text-center">
            <a href="/?ai=1" onClick={(e) => preventDefault(e, () => onStartAiInterpretation())} className="text-emerald-400 hover:text-emerald-300 text-sm font-semibold no-underline">
              أو جرّب التفسير الآن على الويب مجاناً ←
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AppPage;